import gql from 'graphql-tag';

export const TICKET_FRAGMENT = gql`
	fragment ticket on Ticket {
		id
		identifier
		status
		archived
		description
		lastRepeated
		repeatCount
		resolvedAt
		notifyWhenResolved
		requestorName
		requestMethod
		refundNeeded
		refundAmount
		requestorContacted
		requestorContactInfo
		marketFeedback
		alert {
			id
			name
			destroyed
		}
		ticketSchedule {
			id
			name
			destroyed
		}
		assignee {
			id
			firstname
			lastname
			destroyed
		}
		resolver {
			id
			firstname
			lastname
			destroyed
		}
		type
		priority
		machine {
			id
			assetId
			type
			place {
				id
				name
			}
			location {
				id
				name
			}
			destroyed
		}
		notes {
			id
			description
			isAdminNote
			created
			creator {
				id
				firstname
				lastname
			}
		}
		created
		creator {
			id
			firstname
			lastname
			destroyed
		}
		tags {
			id
			name
		}
		visibleToDrivers
		photoRequired
		autoIncludeOnNextRun
		ticketWorkflowStatus
		ticketAssignmentCategory
		serviceTag
		outsourceEmailRecipient
		outsourcedAt
		outsourcedEmailQueuedAt
		repairWorkCarriedOut
		repairPartsUsed
		repairOutcome
		repairCompletedByName
		repairCompletedAt
		repairReportUrl
		resolvedAutomatically
		imageUrls
		serviceRunId

		timeSpent
		timeSpentUnit
		labourCost
		partsCost
	}
`;

// Card payload for the triage board: five column queries poll, so keep notes/repair/costs out.
export const TICKET_TRIAGE_CARD_FRAGMENT = gql`
	fragment ticketTriageCard on Ticket {
		id
		identifier
		status
		archived
		description
		created
		resolvedAt
		imageUrls
		type
		priority
		photoRequired
		notifyWhenResolved
		serviceTag
		autoIncludeOnNextRun
		ticketWorkflowStatus
		ticketAssignmentCategory
		outsourceEmailRecipient
		outsourcedEmailQueuedAt
		visibleToDrivers
		machine {
			id
			assetId
			type
			destroyed
			place {
				id
				name
			}
			location {
				id
				name
			}
		}
		assignee {
			id
			firstname
			lastname
			destroyed
		}
		resolver {
			id
			firstname
			lastname
			destroyed
		}
		tags {
			id
			name
		}
	}
`;

export const GET_TICKETS_FOR_TRIAGE_BOARD = gql`
	query(
		$page: Int!,
		$perPage: Int!,
		$sortBy: String!,
		$sortDirection: Int!,
		$filter: TicketPageFilter
	) {
		getTickets(
			page: $page,
			perPage: $perPage,
			sortBy: $sortBy,
			sortDirection: $sortDirection,
			filter: $filter
		) {
			items {
				...ticketTriageCard
			}
			count
		}
	}
	${TICKET_TRIAGE_CARD_FRAGMENT}
`;

export const GET_TICKET_TRIAGE_RESOLVE_STATS = gql`
	query getTicketTriageResolveStats {
		getTicketTriageResolveStats {
			inHouse {
				count
				averageResolveMs
			}
			outsourced {
				count
				averageResolveMs
			}
		}
	}
`;

export const MOVE_TICKET_ON_TRIAGE_BOARD = gql`
	mutation moveTicketOnTriageBoard($input: MoveTicketOnTriageBoardInput!) {
		moveTicketOnTriageBoard(input: $input) {
			...ticketTriageCard
		}
	}
	${TICKET_TRIAGE_CARD_FRAGMENT}
`;

export const GET_TICKETS = gql`
	query(
		$page: Int!,
		$perPage: Int!,
		$sortBy: String!,
		$sortDirection: Int!,
		$filter: TicketPageFilter
	) {
		getTickets(
			page: $page,
			perPage: $perPage,
			sortBy: $sortBy,
			sortDirection: $sortDirection,
			filter: $filter
		) {
			items {
				...ticket
			}
			count
		}
	}
	${TICKET_FRAGMENT}
`;

export const GET_DRIVER_MY_DAY_TICKET_ITEMS = gql`
	query(
		$page: Int!,
		$perPage: Int!,
		$sortBy: String!,
		$sortDirection: Int!
	) {
		getDriverMyDayTicketItems(
			page: $page,
			perPage: $perPage,
			sortBy: $sortBy,
			sortDirection: $sortDirection
		) {
			items {
				id
				notResolved
				notResolvedComment
				ticket {
					...ticket
				}
			}
			count
		}
	}
	${TICKET_FRAGMENT}
`;

export const GET_ALL_TICKETS = gql`
	query(
		$sortBy: String!,
		$sortDirection: Int!,
		$filter: TicketPageFilter
	) {
		getTickets(
			sortBy: $sortBy,
			sortDirection: $sortDirection,
			filter: $filter
		) {
			items {
				...ticket
			}
			count
		}
	}
	${TICKET_FRAGMENT}
`;

export const DESTROY_TICKET = gql`
	mutation destroyTicketMutation ($id: ID!) {
		destroyTicket(id: $id)
	}
`;

export const GET_TICKET = gql`
	query($id: String) {
		getTicket(id: $id) {
			...ticket
		}
	}
	${TICKET_FRAGMENT}
`;

export const MODIFY_TICKET = gql`
	mutation modifyTicketMutation($input: ModifyTicketInput!) {
		modifyTicket(input: $input) {
			ticket {
				...ticket
			}
		}
	}
	${TICKET_FRAGMENT}
`;

export const SUBSCRIBE_TO_TICKET = gql`
	subscription ($input: TicketSubscriptionInput!) {
		TicketSubscription(input: $input) {
			ticket {
				...ticket
			}
			destroyedTicketId
		}
	}
	${TICKET_FRAGMENT}
`;

export const TICKET_RESOLVE_MUTATION = gql`
	mutation resolveTicket ($id: ID!, $adminTraining: Boolean, $serviceDocketId: ID) {
		resolveTicket (id: $id, adminTraining: $adminTraining, serviceDocketId: $serviceDocketId) {
			id
			assignee {
				id
				firstname
				lastname
				destroyed
			}
			resolver {
				id
				firstname
				lastname
				destroyed
			}
			resolvedAt
			status
			ticketWorkflowStatus
			ticketAssignmentCategory
			serviceTag
			resolvedAutomatically
		}
	}
`;

export const TICKET_CANT_RESOLVE_MUTATION = gql`
	mutation cantResolveTicket ($ticketListItemId: ID!, $notResolvedComment: String, $adminTraining: Boolean, $serviceDocketId: ID) {
		cantResolveTicket (ticketListItemId: $ticketListItemId, notResolvedComment: $notResolvedComment, adminTraining: $adminTraining, serviceDocketId: $serviceDocketId) {
			id
			notResolved
			notResolvedComment
		}
	}
`;

const TICKET_AI_SUGGESTION_FRAGMENT = gql`
	fragment TicketAISuggestionFragment on TicketAISuggestion {
		id
		inputDescription
		suggestedDescription
		suggestedPriority
		confidence
		missingDetails
		status
		appliedFields
		suggestedType {
			id
			label
			category
		}
	}
`;

export const GENERATE_TICKET_AI_SUGGESTION = gql`
	mutation generateTicketAISuggestion($input: TicketAISuggestionInput!) {
		generateTicketAISuggestion(input: $input) {
			suggestion {
				...TicketAISuggestionFragment
			}
		}
	}
	${TICKET_AI_SUGGESTION_FRAGMENT}
`;

export const ACK_TICKET_AI_SUGGESTION = gql`
	mutation acknowledgeTicketAISuggestion($id: ID!, $input: TicketAISuggestionAcknowledgeInput!) {
		acknowledgeTicketAISuggestion(id: $id, input: $input) {
			...TicketAISuggestionFragment
		}
	}
	${TICKET_AI_SUGGESTION_FRAGMENT}
`;

export const TICKET_REOPEN_MUTATION = gql`
	mutation reopenTicket ($id: ID!) {
		reopenTicket (id: $id) {
			id
			resolver {
				id
				firstname
				lastname
				destroyed
			}
			resolvedAt
			status
			ticketWorkflowStatus
			ticketAssignmentCategory
			serviceTag
			resolvedAutomatically
		}
	}
`;

export const TICKET_TOGGLE_SHOW_IN_RUNS_MUTATION = gql`
	mutation ticketToggleVisibleToDrivers ($id: ID!) {
		ticketToggleVisibleToDrivers (id: $id) {
			id
			visibleToDrivers
		}
	}
`;

// Ticket preview
export const TICKET_PREVIEW_FRAGMENT = gql`
	fragment ticketPreview on Ticket {
		id
		description
		identifier
		notes {
			id
			description
			isAdminNote
		}
		created
		lastRepeated
		repeatCount
	}
`;

export const GET_TICKETS_FOR_PREVIEW = gql`
	query(
		$filter: TicketPageFilter
	) {
		getTickets(
			filter: $filter
		) {
			items {
				...ticketPreview
			}
			count
		}
	}
	${TICKET_PREVIEW_FRAGMENT}
`;

export { CREATE_TICKET } from './createTicket';

export const UPDATE_TICKET_PHOTOS = gql`
	mutation updateTicketPhotos ($id: ID, $imageUrls: [String]) {
		updateTicketPhotos (id: $id, imageUrls: $imageUrls) {
			id
			imageUrls
		}
	}
`;

export const REMOVE_TICKET_PHOTO = gql`
	mutation removeTicketPhoto ($id: ID!, $imageUrl: String!) {
		removeTicketPhoto (id: $id, imageUrl: $imageUrl) {
			id
			imageUrls
		}
	}
`;
